import React, { type PropsWithChildren, useCallback } from 'react';

import {
	COMMON_TAGS,
	ErrorBoundary as CommonErrorBoundary,
	getSentryClient,
	UNKNOWN_TAG,
} from '@atlassian/dynamic-cards-search-common';

import { useBootstrap } from '../store';

export type onError = (error: any) => Promise<void>;

export const useLogException = () => {
	const [{ user, primaryProduct }] = useBootstrap();

	return useCallback(
		async (error) => {
			try {
				const SentryClient = await getSentryClient();

				if (!SentryClient) {
					return;
				}

				SentryClient.withScope((scope) => {
					for (const [key, value] of Object.entries(COMMON_TAGS)) {
						scope.setTag(key, value);
					}
					scope.setUser({ id: user?.id || UNKNOWN_TAG });
					scope.setTag('product', primaryProduct || UNKNOWN_TAG);
					SentryClient.captureException(error, scope);
				});
			} catch (error) {
				// When there was an error with Sentry, just output to console
				// eslint-disable-next-line no-console
				console.error(error);
			}
		},
		[primaryProduct, user?.id],
	) satisfies onError;
};

type ErrorBoundaryProps = {
	fallback?: React.ReactNode;
};

export const ErrorBoundary = (props: PropsWithChildren<ErrorBoundaryProps>) => {
	const onException = useLogException();

	return (
		<CommonErrorBoundary {...props} onException={onException}>
			{props.children}
		</CommonErrorBoundary>
	);
};
